import React from 'react'
import { X } from 'lucide-react'
import { Brand, Category, Size } from '@/api'

interface TagListProps {
  selectedGender: string
  selectedCategories: string[]
  selectedConditions: string[]
  selectedSizes: string[]
  brand?: Brand
  categories?: Category[]
  sizes?: Size[]
  genderOptions: { id: string; label: string }[]
  onGenderChange: (gender: string) => void
  onCategoryChange: (categories: string[]) => void
  onConditionChange: (conditions: string[]) => void
  onSizeChange: (sizes: string[]) => void
}

type Tag = {
  id: string
  name: string
  type: 'gender' | 'category' | 'condition' | 'size'
  value: string
}

export const TagList: React.FC<TagListProps> = ({
  selectedGender,
  selectedCategories,
  selectedConditions,
  selectedSizes,
  brand,
  categories,
  sizes,
  genderOptions,
  onGenderChange,
  onCategoryChange,
  onConditionChange,
  onSizeChange,
}) => {
  const tags: Tag[] = [
    selectedGender && {
      id: `gender-${selectedGender}`,
      name:
        genderOptions.find((option) => option.id === selectedGender)?.label ??
        '',
      type: 'gender',
      value: selectedGender,
    },
    ...selectedCategories.map((id) => ({
      id: `category-${id}`,
      name: categories?.find((cat) => cat.id === id)?.name ?? '',
      type: 'category',
      value: id,
    })),
    ...selectedConditions.map((id) => ({
      id: `condition-${id}`,
      name: brand?.conditions?.find((cond) => cond.id === id)?.name ?? '',
      type: 'condition',
      value: id,
    })),
    ...selectedSizes.map((id) => ({
      id: `size-${id}`,
      name: sizes?.find((size) => size.id === id)?.name ?? '',
      type: 'size',
      value: id,
    })),
  ].filter((tag): tag is Tag => Boolean(tag && tag.name))

  const handleRemoveTag = (tag: Tag) => {
    const actions = {
      gender: () => onGenderChange(''),
      category: () =>
        onCategoryChange(selectedCategories.filter((id) => id !== tag.value)),
      condition: () =>
        onConditionChange(selectedConditions.filter((id) => id !== tag.value)),
      size: () => onSizeChange(selectedSizes.filter((id) => id !== tag.value)),
    }
    actions[tag.type]()
  }

  if (tags.length === 0) return null

  return (
    <div className="flex flex-wrap gap-x-1 gap-y-1.5 px-4 sm:pl-[30px] sm:pr-[20px] pt-5">
      {tags.map((tag) => (
        <div
          key={tag.id}
          className="flex items-center gap-1 px-2 py-1 border-gray-500 border text-xs sm:text-sm"
        >
          <span>{tag.name}</span>
          <button
            type="button"
            aria-label={`Remove ${tag.name} filter`}
            className="flex items-center justify-center"
            onClick={() => handleRemoveTag(tag)}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  )
}
